import React, { useEffect, useState } from 'react'

import { Loader2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import { useTranslation } from '@/core/i18n/useTranslation'
import { PageLayout } from '@/shared/components/organisms/Layout/PageLayout/PageLayout'

import { Integration } from '@core/api/entities/integration'

import EmptyState from './components/EmptyState'
import IntegrationsGrid from './components/IntegrationsGrid'
import IntegrationsHeader from './components/IntegrationsHeader'
import TypeformModal from './components/TypeformModal'

export interface IntegrationsPageUIProps {
	integrations: Integration[]
	isLoading: boolean
	error: Error | null
	searchValue: string
	activeTab: 'integrations' | 'addons'
	isTypeformModalOpen: boolean
	onCloseTypeformModal: () => void
	onSearchChange: (value: string) => void
	onTabChange: (tab: 'integrations' | 'addons') => void
}

const IntegrationsPageUI: React.FC<IntegrationsPageUIProps> = ({
	integrations,
	isLoading,
	error,
	searchValue,
	activeTab,
	isTypeformModalOpen,
	onCloseTypeformModal,
	onSearchChange,
	onTabChange,
}) => {
	const { t } = useTranslation('common')
	const navigate = useNavigate()
	const [isDesktop, setIsDesktop] = useState(() => {
		if (typeof window === 'undefined') return true
		return window.matchMedia('(min-width: 768px)').matches
	})
	useEffect(() => {
		const mq = window.matchMedia('(min-width: 768px)')
		const onChange = () => setIsDesktop(mq.matches)
		mq.addEventListener('change', onChange)
		return () => mq.removeEventListener('change', onChange)
	}, [])
	const showEmptyState = !isLoading && (integrations.length === 0 || error)
	const pageTitle = isDesktop
		? t('integrations.title')
		: t('integrations.title_mobile')

	const handleBack = () => {
		if (window.history.length > 1) {
			navigate(-1)
			return
		}
		navigate('/account')
	}

	return (
		<PageLayout
			title={pageTitle}
			backButton={{ show: true, onClick: handleBack }}
		>
			<IntegrationsHeader
				searchValue={searchValue}
				onSearchChange={onSearchChange}
				activeTab={activeTab}
				onTabChange={onTabChange}
			/>
			{isLoading && (
				<div className="flex items-center justify-center py-12">
					<Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
				</div>
			)}

			{showEmptyState && <EmptyState hasSearch={!!searchValue} />}

			{!isLoading && !showEmptyState && (
				<IntegrationsGrid integrations={integrations} activeTab={activeTab} />
			)}

			<TypeformModal
				isOpen={isTypeformModalOpen}
				onClose={onCloseTypeformModal}
			/>
		</PageLayout>
	)
}

export default IntegrationsPageUI
